<template>
  <dd-form-page
      :bread-crumbs="breadCrumbs"
      :header="header"
      @submit="onSubmit"
  >
    <template #steps>
      <dd-step title="Instellingen">
        <div class="flex-col gap-y-md">
          <dd-header-card header="Proces type">
            <template v-if="createCampaign">
              Campagne
            </template>
            <template v-if="createDataEnrichment">
              Data verrijking
            </template>
            <template v-if="createSelection">
              Standaard selectie
            </template>
          </dd-header-card>
          <dd-header-card header="Dataverrijking instellingen" v-if="createDataEnrichment">
            <div class="action" v-if="hasAutomationAction">
              <p>{{ automationAction.name }}</p>
              <OnlyGroupActionSettingsComponent
                  v-if="hasGroupAction"
                  v-bind:settings="groupAction.settings"
                  v-bind:errors="{}"
                  v-on:change="onSettingsChange"
                  v-on:input="onSettingsChange"
                  v-bind:loading="false"
              />
            </div>
          </dd-header-card>
          <dd-header-card header="Algemene instellingen">
            <GeneralSettings/>
          </dd-header-card>
          <ChooseAutomationActions/>
        </div>
      </dd-step>
      <dd-step title="Actie instellingen">
        <dd-header-card v-if="send_email || createCampaign" header="E-mail versturen instellingen" class="mb-md">
          <template #icon>
            <EmailIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureEmailSettingsComponent v-model="globalAutomationConfigurationModel"/>
        </dd-header-card>

        <dd-header-card v-if="create_task" header="Taak aanmaken instellingen" class="mb-md">
          <template #icon>
            <CheckboxOutlineIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureTaskSettingsComponent v-model="globalAutomationConfigurationModel"/>
        </dd-header-card>

        <dd-header-card v-if="create_signal" header="Signaal aanmaken instellingen" class="mb-md">
          <template #icon>
            <BellIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureSignalSettingsComponent v-model="globalAutomationConfigurationModel"/>
        </dd-header-card>

        <dd-header-card v-if="!(send_email || createCampaign) && !create_task && !create_signal"
                        header="Geen actie instellingen" class="mb-md">
          <template #icon>
            <InformationOutlineIcon class="icon-size-lg fill-primary"/>
          </template>
          Er zijn geen acties geselecteerd om te automatiseren.
        </dd-header-card>
      </dd-step>
    </template>
  </dd-form-page>
</template>

<script>
import store from '../store'
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex'
import ConfigureTaskSettingsComponent from '../components/ConfigureTaskSettingsComponent.vue'
import ConfigureSignalSettingsComponent from '../components/ConfigureSignalSettingsComponent.vue'
import ConfigureEmailSettingsComponent from '../components/ConfigureEmailSettingsComponent.vue'
import GeneralSettings from '../components/GeneralSettings.vue'
import ChooseAutomationActions from '../components/ChooseAutomationActions.vue'
import OnlyGroupActionSettingsComponent from '~/modules/group_action/OnlyGroupActionSettingsComponent.vue'
import groupActionStore from '~/modules/group_action/store'

export default {
  name: 'EditGlobalScreen',
  components: {
    OnlyGroupActionSettingsComponent,
    ChooseAutomationActions,
    GeneralSettings,
    ConfigureTaskSettingsComponent,
    ConfigureSignalSettingsComponent,
    ConfigureEmailSettingsComponent,
  },
  data () {
    return {
      loaded: false,
      loading: false,
      groupAction: null,
      form: {},
      header: 'Globale automation instellen',
      breadCrumbs: [
        {
          name: 'Automations',
          to: () => this.navigator.navigatePath(this.route('vue.dashboard.automations.index'))
        }
      ]
    }
  },
  beforeCreate () {
    const STORE_KEY = '$_automation'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }

    const GROUP_ACTION_STORE_KEY = '$_group_action'
    // eslint-disable-next-line no-underscore-dangle
    if (!(GROUP_ACTION_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(GROUP_ACTION_STORE_KEY, groupActionStore)
    }
  },
  watch: {
    creatingAutomation: {
      handler () {
        this.setFormDirty(true)
      },
      deep: true
    },
    automationAction: {
      handler () {
        this.handleAutomationActionChange()
      },
      deep: true
    }
  },
  beforeRouteLeave (to, from, next) {
    if (!this.formDirty) {
      next()
      return
    }
    this.$confirm({
      title: 'Weet je het zeker?',
      content: 'Je hebt wijzigingen aangebracht die niet zijn opgeslagen.',
      okText: 'Niet opslaan',
      cancelText: 'Verder bewerken',
      onOk: () => {
        this.resetCreatingAutomation()
        this.resetFormPageErrors()
        next()
      },
      onCancel: () => {}
    })
  },
  activated () {
    this.findOrCreateGlobalAutomationConfigurationByGlobalAutomation(this.automationId)
      .then((response) => {
        this.selectGlobalAutomationConfiguration(response)
        this.setCreatingAutomation(response)
        this.$set(this.breadCrumbs, 1, {
          name: this.title,
          to: () => this.navigator.navigatePath(this.route('vue.dashboard.automations.global.show', { automation: this.automationId }))
        })
        this.header = `${this.title} instellen`
        this.loaded = true
      })
  },
  methods: {
    ...mapActions({
      getGroupActionByAutomationActionId: '$_group_action/getGroupActionByAutomationActionId',
    }),
    ...mapActions('$_automation', [
      'storeAutomation',
      'selectGlobalAutomationConfiguration',
      'updateGlobalAutomationConfiguration',
      'findOrCreateGlobalAutomationConfigurationByGlobalAutomation',
    ]),
    ...mapMutations('$_automation', {
      setCreatingAutomation: 'SET_CREATING_AUTOMATION',
      resetCreatingAutomation: 'RESET_CREATING_AUTOMATION_TO_DEFAULT',
      setFormDirty: 'SET_FORM_DIRTY',
    }),
    ...mapMutations('$_form_page', {
      resetFormPageErrors: 'RESET_AXIOS_ERROR',
    }),
    onSubmit () {
      this.updateGlobalAutomationConfiguration(this.creatingAutomation)
        .then(response => {
          this.setFormDirty(false)
          this.notificationsManager.success(response)
          this.selectGlobalAutomationConfiguration(response.data)
          this.navigator.navigatePath(this.route('vue.dashboard.automations.global.show', {
            automation: this.automationId,
          }))
        })
        .catch(error => {
          this.notificationsManager.error(error)
        })
    },
    handleAutomationActionChange () {
      if (this.automationAction === undefined) {
        return
      }
      this.getGroupActionByAutomationActionId(this.automationAction.id)
        .then(groupAction => {
          this.groupAction = groupAction
        })

      // this.clearExtendingSearches()
    },
    filterOption (input, option) {
      return true;
      (
        option.componentOptions.children[0].text.toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    onSettingsChange (settings) {
      let creatingAutomation = Object.assign({}, this.$store.state.$_automation.creatingAutomation, { automation_action_settings: settings })

      return this.$store.commit('$_automation/SET_CREATING_AUTOMATION', creatingAutomation)
    },
  },
  computed: {
    ...mapGetters('$_automation', [
      'isNextStepEnabled',
      'selectedGlobalAutomation',
      'selectedGlobalAutomationConfiguration',
      'creatingAutomation',
      'automationActions',
      'selectedCreatingAutomationAction',
    ]),
    ...mapGetters('$_form_page', [
      'errorsObject',
    ]),
    ...mapState('$_automation', {
      send_email: (state) => state.creatingAutomation?.send_email,
      create_task: (state) => state.creatingAutomation?.create_task,
      create_signal: (state) => state.creatingAutomation?.create_signal,

      processType: (state) => state.selectedGlobalAutomation?.type,
      formDirty: (state) => state.formDirty,
    }),
    automationAction () {
      return this.selectedGlobalAutomation?.automation_action?.data
    },
    globalAutomationConfigurationModel: {
      get () {
        return this.creatingAutomation
      },
      set (formConfig) {
        this.setCreatingAutomation(formConfig)
      }
    },
    hasAutomationAction () {
      return this.automationAction !== null && this.automationAction !== undefined
    },
    hasGroupAction () {
      return this.groupAction !== null && this.groupAction !== undefined
    },
    title () {
      return this.selectedGlobalAutomation?.title || 'Automation'
    },
    // edit page for global automation different from the edit page for automation
    automationId () {
      return this.$route.params?.automation
    },
    processType () {
      return this.selectedGlobalAutomation?.type
    },
    createDataEnrichment () {
      return this.processType === 'data_enrichment'
    },
    createCampaign () {
      return this.processType === 'campaign'
    },
    createSelection () {
      return this.processType === 'selection'
    },
  },
}
</script>

<style scoped>
.automation-create-component {
  position: relative;
}
</style>
